import axios from 'axios';
import { API_CONFIG } from '../constants/index.js';

/**
 * Project Service
 * Handles all project-related API operations
 * Uses the global axios interceptors from AuthService for authentication
 */
class ProjectService {
  constructor() {
    // Add fallback values in case API_CONFIG is undefined
    this.baseURL = API_CONFIG?.BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
    this.projectsEndpoint = API_CONFIG?.ENDPOINTS?.PROJECTS || '/api/projects';
  }

  /**
   * Create or update draft project
   * @param {object} data - Project data
   * @param {string|null} projectId - Project ID for updates, null for new draft
   * @returns {Promise<object>} API response
   */
  async saveDraft(data, projectId = null) {
    try {
      const url = projectId
        ? `${this.baseURL}${this.projectsEndpoint}/draft/${projectId}`
        : `${this.baseURL}${this.projectsEndpoint}/draft`;

      const method = projectId ? 'put' : 'post';
      const response = await axios[method](url, data);

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get draft project by ID
   * @param {string} projectId - Project ID
   * @returns {Promise<object>} Project data
   */
  async getDraft(projectId) {
    try {
      const response = await axios.get(`${this.baseURL}${this.projectsEndpoint}/draft/${projectId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get project by ID for editing (works for draft, rejected, and other statuses)
   * @param {string} projectId - Project ID
   * @returns {Promise<object>} Project data
   */
  async getProjectForEdit(projectId) {
    try {
      // First try to get as draft
      try {
        const response = await axios.get(`${this.baseURL}${this.projectsEndpoint}/draft/${projectId}`);
        return response.data;
      } catch (draftError) {
        // If not a draft, get by ID through user projects
        const userProjectsResponse = await axios.get(`${this.baseURL}${API_CONFIG.ENDPOINTS.USERS}/projects`);
        const userProjects = userProjectsResponse.data.data;
        const project = userProjects.find(p => p.id === projectId);

        if (!project) {
          throw new Error('Project not found or you do not have permission to edit it');
        }

        // Get full project details by slug
        const fullProjectResponse = await axios.get(`${this.baseURL}${this.projectsEndpoint}/${project.slug}`);
        return { success: true, data: fullProjectResponse.data.data };
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user's projects
   * @param {string|null} status - Filter by status (optional)
   * @returns {Promise<object>} User's projects
   */
  async getUserProjects(status = null) {
    try {
      const params = status ? { status } : {};
      const response = await axios.get(`${this.baseURL}${this.projectsEndpoint}/user/projects`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user's projects for ProjectsPage (with rich data)
   * @param {string|null} status - Filter by status (optional)
   * @returns {Promise<object>} User's projects with rich data
   */
  async getUserProjectsForProjectsPage(status = null) {
    try {
      const params = status ? { status } : {};
      const response = await axios.get(`${this.baseURL}${this.projectsEndpoint}/user/projects-page`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Submit project for review
   * @param {string} projectId - Project ID
   * @returns {Promise<object>} Submission result
   */
  async submitProject(projectId) {
    try {
      const response = await axios.post(`${this.baseURL}${this.projectsEndpoint}/submit/${projectId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete draft project
   * @param {string} projectId - Project ID
   * @returns {Promise<object>} Deletion result
   */
  async deleteDraft(projectId) {
    try {
      const response = await axios.delete(`${this.baseURL}${this.projectsEndpoint}/draft/${projectId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload project image
   * @param {File} file - Image file
   * @returns {Promise<object>} Upload result with image URL
   */
  async uploadImage(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(
        `${this.baseURL}${this.projectsEndpoint}/upload-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload project video
   * @param {File} file - Video file
   * @param {Function} onUploadProgress - Callback for upload progress
   * @returns {Promise<object>} Upload result with video URL
   */
  async uploadVideo(file, onUploadProgress) {
    try {
      const formData = new FormData();
      formData.append('video', file);

      const response = await axios.post(
        `${this.baseURL}${this.projectsEndpoint}/upload-video`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress
        }
      );

      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete uploaded image
   * @param {string} imageUrl - Image URL to delete
   * @returns {Promise<object>} Deletion result
   */
  async deleteImage(imageUrl) {
    try {
      const response = await axios.delete(`${this.baseURL}${this.projectsEndpoint}/delete-image`, {
        data: { imageUrl }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get project statistics for current user
   * @returns {Promise<object>} Project statistics
   */
  async getProjectStats() {
    try {
      const response = await axios.get(`${this.baseURL}${this.projectsEndpoint}/user/stats`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get public projects with optional filtering
   * @param {object} filters - Filter options (category, search, limit, cursor)
   * @returns {Promise<object>} Public projects list
   */
  async getPublicProjects(filters = {}) {
    try {
      const response = await axios.get(`${this.baseURL}${this.projectsEndpoint}`, { params: filters });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get newest active projects for homepage
   * @returns {Promise<object>} Top 3 newest active projects
   */
  async getNewestProjects() {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      const url = `${baseUrl}/api/projects/newest`;
      const response = await axios.get(url);
      //console.log('Newest projects response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getNewestProjects:', error);
      console.error('Error response:', error.response);
      throw this.handleError(error);
    }
  }

  /**
   * Get project by slug (public)
   * @param {string} slug - Project slug
   * @returns {Promise<object>} Project data
   */
  async getProjectBySlug(slug) {
    try {
      const response = await axios.get(`${this.baseURL}${this.projectsEndpoint}/${slug}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Track project view
   * @param {string} projectId - Project ID
   */
  async trackView(projectId) {
    try {
      await axios.post(`${this.baseURL}${this.projectsEndpoint}/${projectId}/view`);
    } catch (error) {
      // Silently fail for tracking
      console.warn('Failed to track view:', error);
    }
  }

  /**
   * Track project share
   * @param {string} projectId - Project ID
   */
  async trackShare(projectId) {
    try {
      await axios.post(`${this.baseURL}${this.projectsEndpoint}/${projectId}/share`);
    } catch (error) {
      // Silently fail for tracking
      console.warn('Failed to track share:', error);
    }
  }

  /**
   * Resubmit rejected project
   * @param {string} projectId - Project ID
   * @param {object} updatedData - Updated project data (optional)
   * @returns {Promise<object>} Resubmission result
   */
  async resubmitProject(projectId, updatedData = {}) {
    try {
      const response = await axios.post(`${this.baseURL}${this.projectsEndpoint}/resubmit/${projectId}`, updatedData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   * @param {object} error - Axios error object
   * @returns {object} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { data, status } = error.response;
      return {
        message: data?.error?.message || 'An error occurred',
        code: data?.error?.code || 'UNKNOWN_ERROR',
        status,
        details: data?.error?.details
      };
    } else if (error.request) {
      // Network error
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        status: 0
      };
    } else {
      // Other error
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 0
      };
    }
  }
}

// Create and export singleton instance
const projectService = new ProjectService();
export default projectService;
